import { useState, useEffect } from "react";
import {
  SqSelect,
  SqSelectTrigger,
  SqSelectContent,
  SqSelectItem,
} from "@/components/squircle-ui/select";
import { getPlatformIcon } from "@/screens/utils/Helpers";
import { RiQrScanLine, RiEyeOffLine } from "@remixicon/react";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Portal } from "@radix-ui/react-tooltip";
import { PlatformType } from "@/types";

interface Props {
  value: string | null;
  onChange: (value: string | null) => void;
  options: { key: string; label: string; platforms: PlatformType[]; show_platforms: boolean }[];
  triggerClassName?: string;
  triggerInnerClassName?: string;
  hidden?: boolean;
}
const BreakdownBy = ({
  value,
  onChange,
  options,
  triggerClassName,
  triggerInnerClassName,
  hidden,
}: Props) => {
  const selectedOption = options.find((o) => o.key === value);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Reset tooltip state when hidden changes to prevent auto-opening
  useEffect(() => {
    setTooltipOpen(false);
  }, [hidden]);

  return (
    <SqSelect key={value ?? "none"} value={value ?? undefined} onValueChange={onChange}>
      <TooltipProvider>
        <Tooltip
          open={hidden ? tooltipOpen : false}
          onOpenChange={(open) => {
            if (hidden) {
              setTooltipOpen(open);
            }
          }}
        >
          <TooltipTrigger asChild>
            <SqSelectTrigger
              chevronClassName="hidden"
              className={cn(
                "cursor-pointer h-8 w-fit min-w-[116px] max-w-[160px] border-0 p-1 shrink-0 disabled:opacity-100",
                hidden && !value && "disabled:opacity-50",
                triggerClassName,
              )}
              disabled={hidden}
            >
              <div
                className={`w-full relative flex gap-[6px] justify-center items-center px-2 py-1 h-6 ${selectedOption?.key && `border rounded-[6px] ${hidden ? "bg-[#F0F1F5] border-[#CCD1DE]" : "bg-[#FFF0EB] border-[#FFDACC]"}`} ${triggerInnerClassName}`}
              >
                <RiQrScanLine
                  size={14}
                  color={
                    selectedOption?.key ? (hidden ? "#35467A" : "#FF4500") : `#9AA3BD`
                  }
                  className="shrink-0"
                />
                <span
                  className={`truncate font-medium not-italic text-[12px] leading-[19.16px] tracking-[0.02em]  translate-y-[1px] ${selectedOption?.key ? (hidden ? "text-[#35467A]" : "text-brand-orange") : "text-[#021859CC]"}`}
                >
                  {selectedOption?.key ? selectedOption.label : "Add Breakdown"}
                </span>
                {hidden && value && (
                  <div
                    className="
                      absolute top-[-12px] right-[-12px]
                      flex justify-center items-center
                      w-6 h-6 aspect-square
                      rounded-[22px] border border-[#E7E8E9]
                      bg-white shadow-sm
                    "
                  >
                    <RiEyeOffLine size={14} className="text-brand-primary-400 shrink-0" />
                  </div>
                )}
              </div>
            </SqSelectTrigger>
          </TooltipTrigger>
          <Portal>
            <TooltipContent side="bottom" align="start">
              <p>
                This view doesn't show the breakdown.
                <br />
                Try bar chart or table view.
              </p>
            </TooltipContent>
          </Portal>
        </Tooltip>
      </TooltipProvider>
      <SqSelectContent radius={12} className="!before:squircle-radius-[12px]">
        <SqTooltipProvider>
          {options?.map((option) => (
            <SqTooltip key={option.key}>
              <SqTooltipTrigger asChild>
                <SqSelectItem
                  bgColor={selectedOption?.key === option.key ? "#F6F6F6" : ""}
                  value={option.key}
                  className="text-brand-primary-new text-[12px] font-normal leading-[19.157px] tracking-[0.24px] cursor-pointer w-full"
                >
                  <div className="w-full flex items-center justify-between gap-2">
                    <span className="flex-shrink-0">
                      {option.label}
                      {option.show_platforms && <span className="text-[#FF4500]">*</span>}
                    </span>
                    {option.show_platforms && (
                      <div className="flex items-center gap-1">
                        {option.platforms.length <= 2 ? (
                          // Show all icons if 2 or fewer platforms
                          option.platforms?.map((p, index) => (
                            <img
                              key={index}
                              className="h-[14px] w-[14px] flex-shrink-0"
                              src={getPlatformIcon(
                                p)}
                            />
                          ))
                        ) : (
                          // Show first icon + count for remaining
                          <>
                            <img
                              className="h-[14px] w-[14px] flex-shrink-0"
                              src={getPlatformIcon(option.platforms[0])}
                            />
                            <span className="translate-y-[1px] text-[#02185999]">
                              +{option.platforms.length - 1}
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </SqSelectItem>
              </SqTooltipTrigger>
              {option.show_platforms && (
                <SqTooltipContent portal={true} side="top" align="start" alignOffset={20}>
                  <p>
                    Available for{" "}
                    {option.platforms
                      .map(
                        (platform) =>
                          platform.charAt(0).toUpperCase() +
                          platform.slice(1).toLowerCase(),
                      )
                      .join(", ")}
                  </p>
                </SqTooltipContent>
              )}
            </SqTooltip>
          ))}
        </SqTooltipProvider>
      </SqSelectContent>
    </SqSelect>
  );
};

export default BreakdownBy;
